import { useEffect, useRef } from 'react';

import { useAuthStore } from '@/features/auth/model/auth-store';

import { authService } from '@/shared/api';
import { useInvalidateUserData } from '@/shared/hooks/use-user-data';
import { analytics } from '@/shared/lib/analytics';
import { logger } from '@/shared/lib/logger';
import { supabaseAuthService } from '@/shared/services/supabase-auth-service';

type AuthProviderProps = {
  children: React.ReactNode;
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const login = useAuthStore((state) => state.login);
  const resetAuthState = useAuthStore((state) => state.resetAuthState);
  const setInitializing = useAuthStore((state) => state.setInitializing);
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isHandlingAuthRef = useRef(false); // Use ref to persist across re-renders

  // Use cached user data hook
  const { invalidateUserData, setUserData, removeUserData } = useInvalidateUserData();

  // Helper function to fetch user data with caching
  // shouldIdentify: true only for initial session (when user first visits with existing session)
  const fetchUserDataWithCache = async (shouldIdentify: boolean = false): Promise<boolean> => {
    try {
      const response = await authService.getMe(); // Use normal flow with tokens

      if (response?.data && 'id' in response.data) {
        logger.auth.debug('User authenticated successfully', { userId: response.data.id });
        login(response.data);
        setUserData(response.data); // Cache the data

        // Identify user in RudderStack only for initial session
        // Login events (email OTP, OAuth) handle identify separately
        if (shouldIdentify) {
          analytics.identify(response.data.id, { email: response.data.email });
        }

        return true;
      } else if (response?.data && response.data.authenticated === false) {
        logger.auth.debug('Backend says not authenticated');
        resetAuthState();
        removeUserData(); // Clear cache
        return false;
      } else {
        logger.auth.error('Unexpected response format from /auth/me', { response });
        resetAuthState();
        removeUserData(); // Clear cache
        return false;
      }
    } catch (error) {
      logger.auth.error('Failed to fetch user data', { error });
      resetAuthState();
      removeUserData(); // Clear cache
      return false;
    }
  };

  useEffect(() => {
    let mounted = true; // Track if component is still mounted
    let initialCheckComplete = false; // Track if initial check is done

    // Set initializing to true at the start
    logger.auth.debug('AuthProvider useEffect: Setting initializing to true');
    setInitializing(true);

    // Add timeout failsafe - if auth check takes more than 10 seconds, force initialization complete
    const initTimeout = setTimeout(async () => {
      if (mounted) {
        logger.auth.warn('Auth initialization timeout - forcing completion');
        setInitializing(false);
        isHandlingAuthRef.current = false;
        initialCheckComplete = true;
      }
    }, 10000);

    // Immediately check current session
    const checkInitialSession = async () => {
      try {
        logger.auth.debug('Checking initial session on mount');
        const { session } = await supabaseAuthService.getSession();

        if (!mounted) return;

        if (session) {
          logger.auth.debug('Initial session found, fetching user data');
          // Call identify for initial session - user visiting with existing session
          const authSuccessful = await fetchUserDataWithCache(true);

          if (!authSuccessful) {
            // Try to refresh the token before giving up
            logger.auth.debug('Initial auth failed, attempting token refresh');
            try {
              const { session: refreshSession } = await supabaseAuthService.refreshSession();
              if (refreshSession) {
                logger.auth.debug('Token refreshed successfully during initial check, retrying');
                // Still initial session, so identify again
                await fetchUserDataWithCache(true);
              }
            } catch (refreshErr) {
              logger.auth.error('Token refresh failed during initial check', { error: refreshErr });
            }
          }
        } else {
          logger.auth.debug('No initial session found');

          // Check for dev session persistence (localhost only)
          if (window.location.hostname === 'localhost') {
            const storedDevUser = localStorage.getItem('famecreator-dev-user');
            if (storedDevUser) {
              try {
                const devUser = JSON.parse(storedDevUser);
                logger.auth.debug('Restoring dev session from localStorage');
                login(devUser);
                // We don't need to fetch user data for dev user as we have it all
                setInitializing(false);
                initialCheckComplete = true;
                clearTimeout(initTimeout);
                return;
              } catch (e) {
                logger.auth.error('Failed to parse stored dev user', { error: e });
                localStorage.removeItem('famecreator-dev-user');
              }
            }
          }

          resetAuthState();
        }

        // Set initializing to false after initial check
        if (mounted) {
          logger.auth.debug('Initial session check complete, setting initializing to false');
          setInitializing(false);
          initialCheckComplete = true;
          clearTimeout(initTimeout);
        }
      } catch (error) {
        logger.auth.error('Error checking initial session', { error });
        if (mounted) {
          setInitializing(false);
          initialCheckComplete = true;
          clearTimeout(initTimeout);
        }
      }
    };

    // Run initial session check
    checkInitialSession();

    // Listen for auth state changes (login, logout, token refresh, initial session)
    const unsubscribe = supabaseAuthService.onAuthStateChange(async (event, session) => {
      logger.auth.debug('Auth state changed', {
        event,
        hasSession: !!session,
        initialCheckComplete,
      });

      // Skip events during initial check to avoid race conditions
      if (!initialCheckComplete) {
        logger.auth.debug('Initial check not complete, skipping event', { event });
        return;
      }

      // Only skip INITIAL_SESSION if we successfully authenticated during manual check
      // This allows INITIAL_SESSION to act as a fallback if manual check failed
      if (event === 'INITIAL_SESSION') {
        const isAuthenticated = useAuthStore.getState().isAuthenticated;
        if (isAuthenticated) {
          logger.auth.debug('Skipping INITIAL_SESSION - already authenticated from manual check');
          return;
        } else {
          logger.auth.debug('Processing INITIAL_SESSION as fallback - manual check did not authenticate');
          // Continue processing this event
        }
      }

      // Prevent handling multiple events simultaneously
      if (isHandlingAuthRef.current && event !== 'SIGNED_OUT') {
        logger.auth.debug('Already handling auth event, skipping', { event });
        return;
      }

      // Clear existing refresh interval
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }

      // Handle auth events - only fetch user data when really necessary
      if (event === 'SIGNED_IN') {
        // Only SIGNED_IN requires immediate user data fetch
        if (session) {
          isHandlingAuthRef.current = true;

          // Set up proactive token refresh every 50 minutes
          // Supabase tokens expire after 1 hour, so we refresh at 50 minutes
          // This ensures tokens are refreshed well before they expire
          if (!refreshIntervalRef.current) {
            logger.auth.debug('Setting up proactive token refresh interval');
            refreshIntervalRef.current = setInterval(async () => {
              try {
                logger.auth.debug('Proactive token refresh triggered');
                const { session, error } = await supabaseAuthService.refreshSession();
                if (error) {
                  logger.auth.error('Proactive token refresh failed', { error });
                } else if (session) {
                  logger.auth.debug('Proactive token refresh successful', {
                    expiresAt: new Date(session.expires_at! * 1000).toISOString(),
                  });
                }
              } catch (error) {
                logger.auth.error('Proactive token refresh error', { error });
              }
            }, 3000000); // Refresh every 50 minutes (3000000ms)
          }

          try {
            logger.auth.debug('SIGNED_IN event - fetching user data');
            await fetchUserDataWithCache();
          } catch (error) {
            logger.auth.error('Failed to fetch user data after auth change', { error, event });
            // Don't logout on error - user might still be authenticated
            // Just reset to not authenticated state
            resetAuthState();
          } finally {
            // ALWAYS set initializing to false after handling
            logger.auth.debug('Finally block: Setting initializing to false', { event });
            setInitializing(false);
            clearTimeout(initTimeout);
            isHandlingAuthRef.current = false;
            logger.auth.debug('Finally block: Done', { event });
          }
        } else {
          // No session - handle all events that might have null session
          logger.auth.debug('No session for auth event', { event });
          // Just reset the auth state without triggering Supabase signOut
          resetAuthState();
          logger.auth.debug('Reset auth state, setting initializing to false', { event });
          setInitializing(false);
          clearTimeout(initTimeout);
        }
      } else if (event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') {
        // For token refresh and user updates, we don't need to call /auth/me
        // Just invalidate the cache so it gets refreshed on next use
        logger.auth.debug('Token refreshed or user updated - invalidating cache', { event });
        invalidateUserData();
        setInitializing(false);
        clearTimeout(initTimeout);
      } else if (event === 'INITIAL_SESSION') {
        // Handle INITIAL_SESSION event if manual check failed
        if (session) {
          isHandlingAuthRef.current = true;
          try {
            logger.auth.debug('INITIAL_SESSION event - fetching user data (fallback)');
            await fetchUserDataWithCache(true); // Identify for initial session
          } catch (error) {
            logger.auth.error('Failed to fetch user data for INITIAL_SESSION', { error });
            resetAuthState();
          } finally {
            setInitializing(false);
            clearTimeout(initTimeout);
            isHandlingAuthRef.current = false;
          }
        } else {
          logger.auth.debug('INITIAL_SESSION with no session');
          resetAuthState();
          setInitializing(false);
          clearTimeout(initTimeout);
        }
      } else if (event === 'SIGNED_OUT') {
        logger.auth.debug('SIGNED_OUT event - resetting auth state');
        // Clear refresh interval on sign out
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
          refreshIntervalRef.current = null;
        }
        resetAuthState();
        removeUserData(); // Clear cached user data
        setInitializing(false);
        clearTimeout(initTimeout);
        isHandlingAuthRef.current = false;
      }
    });

    // Listen for visibility changes to refresh token when user returns to tab
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && initialCheckComplete) {
        logger.auth.debug('Tab became visible, checking session validity');
        try {
          const { session } = await supabaseAuthService.getSession();
          if (session) {
            // Check if token is close to expiry (within 5 minutes)
            const expiresAt = session.expires_at! * 1000;
            const now = Date.now();
            const timeUntilExpiry = expiresAt - now;

            if (timeUntilExpiry < 300000) { // Less than 5 minutes
              logger.auth.debug('Token close to expiry, refreshing', {
                timeUntilExpiry: Math.floor(timeUntilExpiry / 1000) + 's',
              });
              await supabaseAuthService.refreshSession();
            }
          }
        } catch (error) {
          logger.auth.error('Error checking session on visibility change', { error });
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup subscription on unmount
    return () => {
      mounted = false;
      unsubscribe();
      clearTimeout(initTimeout);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      // Clear refresh interval on unmount
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- Zustand functions are stable references

  return <>{children}</>;
};
